import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';

import {
  getCurrentUser,
  login,
  logout,
} from './services/authService';

import {
  ROUTES,
} from '../../config/routes';

import {
  ApiError,
  setUnauthorizedHandler,
} from '../../shared/api/apiClient';

const AuthContext = createContext(null);

const AUTH_MODE =
  process.env.REACT_APP_AUTH_MODE || 'local';

const IS_LOCAL_AUTH =
  AUTH_MODE === 'local';

const LOCAL_ADMIN_ID =
  process.env.REACT_APP_LOCAL_ADMIN_ID ||
  'admin';

const LOCAL_ADMIN_PASSWORD =
  process.env.REACT_APP_LOCAL_ADMIN_PASSWORD ||
  '1234';

const LOCAL_SESSION_STORAGE_KEY =
  'hanwha-semitech-platform:local-session';

const SUPPORTED_LOCAL_ROLES = Object.freeze([
  'ADMIN',
  'USER',
]);

function normalizeRole(role) {
  const normalizedRole = String(role || '')
    .trim()
    .toUpperCase();

  if (
    SUPPORTED_LOCAL_ROLES.includes(
      normalizedRole,
    )
  ) {
    return normalizedRole;
  }

  return 'USER';
}

function createLocalUser(role = 'ADMIN') {
  const normalizedRole = normalizeRole(role);

  return {
    id: 1,
    userId: LOCAL_ADMIN_ID,

    name:
      normalizedRole === 'ADMIN'
        ? '로컬 관리자'
        : '로컬 일반 사용자',

    role: normalizedRole,

    company: {
      name: '한화세미텍',
      address:
        '경상남도 창원시 성산구 정동로 84',
      email: 'company@hanwha.com',
    },

    salesRepresentative: {
      name: '한영업',
      email: 'sales@hanwha.com',
    },

    isLocalUser: true,
  };
}

function readLocalSession() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storedSession =
      window.localStorage.getItem(
        LOCAL_SESSION_STORAGE_KEY,
      );

    if (!storedSession) {
      return null;
    }

    const parsedSession =
      JSON.parse(storedSession);

    if (
      parsedSession?.authenticated !== true ||
      !parsedSession?.user
    ) {
      return null;
    }

    return {
      ...parsedSession.user,
      role: normalizeRole(
        parsedSession.user.role,
      ),
    };
  } catch {
    return null;
  }
}

function saveLocalSession(user) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      LOCAL_SESSION_STORAGE_KEY,
      JSON.stringify({
        authenticated: true,
        user,
        signedInAt:
          new Date().toISOString(),
      }),
    );
  } catch {
    /*
     * 로컬 저장소 사용이 차단된 경우에도
     * 현재 브라우저 세션의 로그인은 유지합니다.
     */
  }
}

function removeLocalSession() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(
      LOCAL_SESSION_STORAGE_KEY,
    );
  } catch {
    // 저장소 삭제 실패는 무시합니다.
  }
}

function createInitialAuthState() {
  if (IS_LOCAL_AUTH) {
    const storedUser =
      readLocalSession();

    if (storedUser) {
      return {
        status: 'authenticated',
        user: storedUser,
        error: null,
      };
    }

    return {
      status: 'unauthenticated',
      user: null,
      error: null,
    };
  }

  return {
    status: 'loading',
    user: null,
    error: null,
  };
}

export function AuthProvider({
  prerendered = false,
  children,
}) {
  const [authState, setAuthState] =
    useState(() => prerendered
      ? { status: 'unauthenticated', user: null, error: null }
      : createInitialAuthState());

  const [isSessionRestoring, setSessionRestoring] = useState(prerendered && !IS_LOCAL_AUTH);
  const authStateRef = useRef(authState);

  useEffect(() => {
    authStateRef.current = authState;
  }, [authState]);

  const forceUnauthenticated =
    useCallback(() => {
      if (IS_LOCAL_AUTH) {
        removeLocalSession();
      }

      const isAlreadyUnauthenticated =
        authStateRef.current.status ===
          'unauthenticated' &&
        !authStateRef.current.user;

      if (isAlreadyUnauthenticated) {
        return;
      }

      setAuthState({
        status: 'unauthenticated',
        user: null,
        error: null,
      });
    }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (
        typeof window !== 'undefined' &&
        window.location.pathname === ROUTES.login
      ) {
        return;
      }

      forceUnauthenticated();

      if (typeof window !== 'undefined') {
        const loginPath = ROUTES.login;
        const currentPath =
          window.location.pathname;

        if (currentPath !== loginPath) {
          window.location.replace(loginPath);
        }
      }
    });

    return () => {
      setUnauthorizedHandler(null);
    };
  }, [forceUnauthenticated]);

  useEffect(() => {
    if (IS_LOCAL_AUTH) {
      if (prerendered) setAuthState(createInitialAuthState());
      return undefined;
    }

    let isActive = true;

    const restoreServerSession =
      async () => {
        try {
          const user =
            await getCurrentUser();

          if (!isActive) {
            return;
          }

          setAuthState({
            status: user
              ? 'authenticated'
              : 'unauthenticated',

            user,
            error: null,
          });
        } catch (error) {
          if (!isActive) {
            return;
          }

          setAuthState({
            status: 'error',
            user: null,
            error,
          });
        } finally {
          if (isActive) setSessionRestoring(false);
        }
      };

    restoreServerSession();

    return () => {
      isActive = false;
    };
  }, [prerendered]);

  const refreshAuth =
    useCallback(async () => {
      if (IS_LOCAL_AUTH) {
        const storedUser =
          readLocalSession();

        setAuthState({
          status: storedUser
            ? 'authenticated'
            : 'unauthenticated',

          user: storedUser,
          error: null,
        });

        return storedUser;
      }

      setAuthState((previousState) => ({
        ...previousState,
        status: 'loading',
        error: null,
      }));

      try {
        const user =
          await getCurrentUser();

        setAuthState({
          status: user
            ? 'authenticated'
            : 'unauthenticated',

          user,
          error: null,
        });

        return user;
      } catch (error) {
        setAuthState({
          status: 'error',
          user: null,
          error,
        });

        return null;
      }
    }, []);

  const signIn =
    useCallback(async ({
      userId,
      password,
    }) => {
      const normalizedUserId =
        String(userId || '').trim();

      const normalizedPassword =
        String(password || '');

      if (IS_LOCAL_AUTH) {
        const isValidCredential =
          normalizedUserId ===
            LOCAL_ADMIN_ID &&
          normalizedPassword ===
            LOCAL_ADMIN_PASSWORD;

        if (!isValidCredential) {
          throw new ApiError(
            'Invalid credentials',
            {
              status: 401,
              code: 'INVALID_CREDENTIALS',
            },
          );
        }

        const user =
          createLocalUser('ADMIN');

        saveLocalSession(user);

        setAuthState({
          status: 'authenticated',
          user,
          error: null,
        });

        return {
          user,
          local: true,
          redirectUrl: '/memberInfo',
        };
      }

      const response = await login({
        userId: normalizedUserId,
        password: normalizedPassword,
      });

      // 로그인 응답에 user가 있어도 쿠키 세션이 생성되지 않았을 수 있어
      // 항상 /me로 실제 세션 유효성을 재확인합니다.
      const user = await getCurrentUser();

      if (!user) {
        throw new ApiError(
          'Authentication session was not created.',
          {
            code:
              'INVALID_AUTH_RESPONSE',
          },
        );
      }

      setAuthState({
        status: 'authenticated',
        user,
        error: null,
      });

      return {
        ...response,
        user,
      };
    }, []);

  const signOut =
    useCallback(async () => {
      if (IS_LOCAL_AUTH) {
        removeLocalSession();

        setAuthState({
          status: 'unauthenticated',
          user: null,
          error: null,
        });

        return;
      }

      try {
        await logout();
      } catch (error) {
        if (
          process.env.NODE_ENV !==
          'production'
        ) {
          console.error(
            '[Auth] 로그아웃 요청 실패:',
            error,
          );
        }
      } finally {
        setAuthState({
          status: 'unauthenticated',
          user: null,
          error: null,
        });
      }
    }, []);

  const contextValue = useMemo(
    () => ({
      isSessionRestoring,
      user: authState.user,
      status: authState.status,
      error: authState.error,

      isAuthenticated:
        authState.status ===
        'authenticated',

      isLocalAuth: IS_LOCAL_AUTH,

      /*
       * 기존 memberInfoPage 코드와의
       * 호환성을 위해 유지합니다.
       */
      isAuthBypassed: IS_LOCAL_AUTH,

      signIn,
      signOut,
      refreshAuth,
    }),
    [
      authState,
      isSessionRestoring,
      signIn,
      signOut,
      refreshAuth,
    ],
  );

  return (
    <AuthContext.Provider
      value={contextValue}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context =
    useContext(AuthContext);

  if (!context) {
    throw new Error(
      'useAuth는 AuthProvider 내부에서 사용해야 합니다.',
    );
  }

  return context;
}
